import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getGoalById, createGoal, editGoal } from "../utils/methods";
import { QuillEditor, InputComp } from "../components";
import { strings } from "../utils/strings";
import UserIcon from "../components/Icons/userIcon";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";
import moment from "moment";
import { usePersistData } from "../store";
import emailjs from "emailjs-com";
import { collectionGroup } from "firebase/firestore";

const GoalEdit = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [goalData, setGoalData] = useState({});
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [disable, setDisable] = useState("");
  const [selectedLineManagerId, setSelectedLineManagerId] = useState("");
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [disableEmployeeSelection, setDisableEmployeeSelection] =
    useState(false);
  const { user } = usePersistData();

  const { lineManager, loading, error } = useFetchOptions();

  useEffect(() => {
    const loadGoalData = async () => {
      if (currentId !== "new") {
        try {
          const goal = await getGoalById(currentId);
          setGoalData(goal);
          setName(goal.name || "");
          setType(goal.type || "");
          setTitle(goal.title || "New Goal");
          setSelectedEmpId(goal.empId || "");
          setSelectedLineManagerId(goal.managerId || "");
          setDisableEmployeeSelection(true);
        } catch (error) {
          console.error("Error loading goal:", error);
        }
      }
    };
    loadGoalData();
  }, [currentId]);

  const handleTitleChange = (key, newTitle) => {
    setTitle(newTitle);
    setGoalData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleInputChange = (field, value) => {
    setGoalData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleDescChange = (key, newValue) => {
    setGoalData((prevData) => ({ ...prevData, [key]: newValue }));
  };

  const handleOptionChange = (key, newValue) => {
    setGoalData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));

    switch (key) {
      case "employeeName":
        setName(newValue || "");
        break;
      case "type":
        setType(newValue || "");
        break;
      default:
        break;
    }
  };

  const handleSave = async () => {
    const progress = goalData.progress || 0;

    // Check if progress is out of range and show toast
    if (progress < 0) {
      toast.error("Progress cannot be less than 0.");
      return;
    }
    if (progress > 100) {
      toast.error("Progress cannot be greater than 100.");
      return;
    }

    const fieldsToValidate = [
      {
        key: "title",
        value: goalData.title,
        label: strings["inputTitle"],
        regex: null,
      },
      {
        key: "progress",
        value: progress,
        label: strings["progress"],
        regex: null,
      },
      {
        key: "employeeName",
        value: currentId === "new" ? name : goalData.employeeName,
        label: strings["name"],
        regex: null,
      },
      {
        key: "detail",
        value: goalData.detail,
        label: "Goal's Content",
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value && value !== 0) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });

    if (!isFormValid) return;

    try {
      const timestamp = moment().toISOString();

      const goalDataWithExtras = {
        ...goalData,
        progress,
        title: goalData.title,
        empId: selectedEmpId,
        managerId: selectedLineManagerId,
        createdAt: timestamp,
        email: currentId === "new" ? email : goalData.email,
        employeeName: currentId === "new" ? name : goalData.employeeName,
      };

      if (currentId === "new") {
        const newGoalId = await createGoal(goalDataWithExtras);

        if (email && goalData.employeeName) {
          const sendEmail = () => {
            const templateParams = {
              to_email: [
                ...(user.email !== goalData.employeeName
                  ? [goalData.employeeName]
                  : [user.email]),
              ],
              // goallink: `https://hr.exeonic.com/goalsDetails/${goalDataWithExtras.managerId}`,
              goallink: `https://hr.exeonic.com/goalsOrOkHours`,
            };

            emailjs
              .send(
                "service_kvpzs4f", // EmailJS service ID
                "template_xuds4dq", // EmailJS template ID
                templateParams,
                "2VV1cH6-a0kM9RqRD" // Your emailjs user ID
              )
              .then(
                (result) => {
                  console.log("Email sent successfully: ", result.text);
                },
                (error) => {
                  console.error("Error sending email: ", error.text);
                }
              );
          };

          sendEmail();
        }
        setGoalData((prevData) => ({
          ...prevData,
          id: newGoalId,
        }));
      } else {
        await editGoal(currentId, goalDataWithExtras);

        if (goalData.email) {
          const sendEmail = () => {
            const templateParams = {
              to_email: [
                ...(user.email !== goalData.email
                  ? [goalData.email]
                  : [user.email]),
              ],
              // goallink: `https://hr.exeonic.com/goalsDetails/${goalData.managerId}`,
              goallink: `https://hr.exeonic.com/goalsOrOkHours`,
            };

            emailjs
              .send(
                "service_kvpzs4f", // EmailJS service ID
                "template_xuds4dq", // EmailJS template ID
                templateParams,
                "2VV1cH6-a0kM9RqRD" // Your emailjs user ID
              )
              .then(
                (result) => {
                  console.log("Email sent successfully: ", result.text);
                },
                (error) => {
                  console.error("Error sending email: ", error.text);
                }
              );
          };

          sendEmail();
        }
      }

      navigate(`/goalsOrOkHours`);
    } catch (error) {
      setDisable(false);
      console.error("Error saving goal:", error);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;

  return (
    <div className="bg-border w-full h-full overflow-scroll xl:px-[212px] lg:px-[100px] md:px-[60px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">
        {currentId === "new"
          ? `${strings["addGoalTitle"]} ${goalData?.title || "New"}`
          : `${strings["editGoalTitle"]} ${goalData?.title}`}
      </p>

      {/* Title */}
      <InputComp
        label={strings["goals"]}
        placeHolder={strings["goalPlaceholder"]}
        type="text"
        value={title}
        handleChange={handleTitleChange}
        inputKey="title"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      {/* Description */}
      <InputComp
        label={strings["progress"]}
        placeHolder={strings["descriptionPlaceholder"]}
        type="number"
        min={0}
        max={100}
        value={goalData?.progress}
        handleChange={handleDescChange}
        inputKey="progress"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["selectEmployee"]}
          </label>
        </div>

        <Autocomplete
          className="w-full"
          options={lineManager.map((user) => user?.email)}
          getOptionLabel={(option) => option || ""}
          value={name}
          onChange={(event, newValue) => {
            if (!disableEmployeeSelection) {
              handleOptionChange("employeeName", newValue || "");

              // Set the selected line manager ID based on the email
              const selectedManager = lineManager.find(
                (manager) => manager.email === newValue
              );

              if (selectedManager) {
                setSelectedLineManagerId(selectedManager.managerId || "");
                setSelectedEmpId(selectedManager.empID || "");
                setEmail(selectedManager.email || "");
              } else {
                setSelectedLineManagerId("");
                setSelectedEmpId("");
                setEmail("");
              }
            }
          }}
          disableClearable
          disabled={disableEmployeeSelection}
          renderOption={(props, option) => (
            <li {...props} className="flex items-center gap-x-2">
              <UserIcon />
              <div className="pt-1">{option}</div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      {/* HTML Editor */}
      <div className="my-6 bg-white ">
        <QuillEditor
          value={goalData?.detail || ""}
          onChange={(content) => handleInputChange("detail", content)}
        />
      </div>

      {/* Upload Button */}
      <div className="flex justify-center w-full">
        <Button
          classes={`!w-full justify-center ${
            disable ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={disable ? null : handleSave}
          disabled={disable}
        >
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default GoalEdit;
